/**
 * AI-Driven Validation page content, transcribed verbatim from
 * WeAreQA-AI-Driven.pdf. One slide per section.
 *
 * The deck is image-only with no text layer, so every string here was read off
 * a rendered page. Text is the deck's; the presentation of it follows
 * design/DESIGN_SYSTEM.md, not the deck's own palette.
 *
 * THE HEADER USED TO COME FROM THE WRONG PLACE. The hero and the "what's
 * included" cards were rendered from `models[0]` in content/models.ts — the
 * approved HOMEPAGE engagement panel — which describes a different service:
 * hallucination benchmarking, prompt-injection suites, regression for
 * non-deterministic systems, i.e. testing AI products somebody else built.
 * This deck is about redesigning your delivery operating model using AI. A
 * reader was promised one service and scrolled into another.
 *
 * `pageHero` and `framework` below replace that, and both are read off the
 * deck. models[0] is untouched, because the homepage still needs it.
 */

import type { IconName } from "../../components/ui/waq/Icon";

/**
 * Hero. Badge from the deck cover, lead from slide 23, stat from slide 5.
 *
 * THE STAT IS A PROBLEM, NOT A CAPABILITY, and it is presented as one — the
 * previous stat ("96 adversarial cases") was a thing we do, so it sat in the
 * yellow treatment the design system reserves for a positive figure. A "70%+
 * fail" number in that treatment reads as a boast about failure. It carries the
 * blue accent instead, and `turn` — the deck's own next line — supplies the
 * yellow, so the panel argues problem → cause rather than just stating a number.
 *
 * SOURCE OUTSTANDING: the deck asserts "70%+ of AI initiatives fail to deliver
 * sustainable impact" with no citation. It is an industry claim, not a client
 * outcome, so it is outside CLAUDE.md's closed metric list — but a CTO will ask
 * where it comes from. Get the source or drop the figure before launch.
 */
export const pageHero = {
  /** Deck cover subtitle. */
  badge: "AI-Native Quality Engineering",
  /** Slide 23. */
  lead: "We help organizations transform software delivery through intelligent quality engineering.",
  /** Slide 5. */
  stat: {
    label: "The problem",
    value: "70%+",
    caption: "of AI initiatives fail to deliver sustainable impact",
    turn: "The reason isn't the technology. It's the way work gets done.",
  },
} as const;

export type Pillar = {
  number: string;
  title: string;
  body: string;
  icon: IconName;
  /** Exactly one pillar is marked. See the note below. */
  highlight: boolean;
};

/**
 * Slide 6 — Our AI-Native Delivery Framework.
 *
 * This is the section that replaces the "what's included" placeholder, and it
 * is worth knowing what it is: **the site links to an "AI-Native Delivery
 * Framework" from eight places, every one of them `href="#"`.** The content for
 * that page has been sitting in this deck the whole time. If that page ever
 * gets built, build it from slide 6 and this section becomes its summary.
 *
 * ONE HIGHLIGHT. Outcomes carries it — it is the only pillar that is a result
 * rather than an input, and the deck itself ends the row there. Same discipline
 * as CaseStudyMetrics' one-highlighted-metric rule; do not mark a second.
 */
export const framework = {
  heading: "Our AI-Native Delivery Framework",
  subhead: "Redesigning Delivery for the AI Era",
  body: "Our framework combines people, processes, platforms, and AI capabilities into a unified, outcome-driven operating model.",
  pillars: [
    {
      number: "01",
      title: "People",
      body: "Empowered teams with AI skills and mindset",
      icon: "users",
      highlight: false,
    },
    {
      number: "02",
      title: "Processes",
      body: "AI-native processes designed for speed, quality and adaptability",
      icon: "gear",
      highlight: false,
    },
    {
      number: "03",
      title: "Platform",
      body: "Unified platform enabling visibility, integration and scale",
      icon: "cube",
      highlight: false,
    },
    {
      number: "04",
      title: "AI Capabilities",
      body: "Embedded intelligence across the delivery value chain",
      icon: "brain",
      highlight: false,
    },
    {
      number: "05",
      title: "Outcomes",
      body: "Measurable business impact and continuous improvement",
      icon: "target",
      highlight: true,
    },
  ] as Pillar[],
  governance: {
    label: "Governance & Quality at the Core",
    icon: "shieldCheck" as IconName,
    items: [
      "Data-driven decisions",
      "Risk management",
      "Compliance",
      "Security",
      "Quality by design",
    ],
  },
  /** The deck's own closing line on this slide. */
  callout:
    "AI is embedded across the entire delivery lifecycle — not as a tool, but as a core capability.",
} as const;

/** Slide 3 — The Software Delivery Landscape Has Changed */
export const deliveryLandscape = {
  eyebrow: "Why Traditional Delivery Models No Longer Scale",
  headingLead: "The Software ",
  headingAccent: "Delivery Landscape",
  headingTail: " Has Changed",
  points: [
    {
      lead: "AI has ",
      accent: "fundamentally changed",
      tail: " how software is built, tested, and delivered.",
    },
    {
      lead: "Yet most organizations still operate with ",
      accent: "traditional processes",
      tail: " and fragmented systems.",
    },
    {
      lead: "These outdated processes have become the ",
      accent: "bottleneck",
      tail: " to speed, quality, and innovation.",
    },
  ],
  /** Icon names mirror the deck's own glyph choices per card and per row. */
  columns: [
    {
      title: "Traditional Delivery",
      icon: "clock",
      highlight: false,
      items: [
        { label: "Manual coordination", icon: "user" },
        { label: "Knowledge silos", icon: "database" },
        { label: "Sequential execution", icon: "sequence" },
        { label: "Slow onboarding", icon: "user" },
      ],
    },
    {
      title: "AI Era",
      icon: "sparkle",
      highlight: true,
      // The deck sets this column as plain statements, with no per-row glyphs.
      items: [
        { label: "AI is everywhere.", icon: null },
        { label: "Expectations are higher.", icon: null },
        { label: "Competition is faster.", icon: null },
        { label: "Change is constant.", icon: null },
      ],
    },
    {
      title: "New Business Expectations",
      icon: "target",
      highlight: false,
      items: [
        { label: "Faster delivery", icon: "rocket" },
        { label: "Higher quality", icon: "shieldCheck" },
        { label: "Lower costs", icon: "cost" },
        { label: "Continuous innovation", icon: "lightbulb" },
      ],
    },
  ],
  callout: {
    lead: "The challenge is no longer AI adoption. The challenge is ",
    accent: "redesigning the operating model.",
  },
} as const;

/** Slide 8 — AI Maturity Model. Slides 5 and 7 skipped at client request. */
export const maturityModel = {
  headingLead: "AI Maturity ",
  headingAccent: "Model",
  eyebrow: "Your Journey to AI-Native Delivery Excellence",
  body: [
    {
      lead: "Our AI Maturity Model defines a clear path from foundational adoption to ",
      accent: "AI-native transformation",
      tail: ".",
    },
    {
      lead: "Each level builds on the previous one, unlocking ",
      accent: "greater value, intelligence, and business impact",
      tail: ".",
    },
  ],
  capabilitiesLabel: "Key capabilities",
  focusLabel: "Focus",
  levels: [
    {
      number: "01",
      name: "Foundational",
      icon: "sprout",
      body: "Establish the basics. Tools and processes are manual and disconnected.",
      capabilities: [
        "Manual testing",
        "Basic documentation",
        "Siloed teams",
        "Limited visibility",
      ],
      focus: "Stability",
    },
    {
      number: "02",
      name: "Emerging",
      icon: "network",
      body: "Introduce automation and standardization. Start integrating tools and data.",
      capabilities: [
        "Test automation",
        "CI/CD integration",
        "Centralized test data",
        "Basic dashboards",
      ],
      focus: "Efficiency",
    },
    {
      number: "03",
      name: "Intelligent",
      icon: "brain",
      body: "Leverage AI to enhance quality engineering and accelerate decision-making.",
      capabilities: [
        "AI-powered test design",
        "Predictive analytics",
        "Self-healing automation",
        "Quality insights",
      ],
      focus: "Optimization",
    },
    {
      number: "04",
      name: "AI-Native",
      icon: "sparkle",
      body: "AI is embedded across processes. Autonomous decision-making at scale.",
      capabilities: [
        "Autonomous testing",
        "AI-driven planning",
        "Risk-based release",
        "Continuous intelligence",
      ],
      focus: "Autonomy",
    },
    {
      number: "05",
      name: "Transformative",
      icon: "rocket",
      body: "AI drives innovation and business outcomes. The organization is future-ready.",
      capabilities: [
        "Generative quality",
        "Self-optimizing delivery",
        "Business outcome prediction",
        "Ecosystem intelligence",
      ],
      focus: "Transformation",
    },
  ],
} as const;

/** Slide 9 — Our AI Transformation Journey. Slides 5, 6 and 7 not used. */
export const transformationJourney = {
  headingLead: "Our AI ",
  headingAccent: "Transformation Journey",
  subheadLead: "A Phased Approach. ",
  subheadAccent: "Measurable Impact at Every Step.",
  bodyLead:
    "Our journey is designed to deliver early value, build momentum, and drive sustainable transformation across ",
  bodyAccents: ["people, processes", "technology"],
  outcomeLabel: "Outcome",
  phases: [
    {
      number: "01",
      title: "Discovery & Foundation",
      tagline: "Build the Right Foundation",
      icon: "search",
      outcomeIcon: "target",
      steps: [
        "Assess current state & maturity",
        "Define strategy & roadmap",
        "Establish governance & operating model",
        "Build foundational capabilities",
        "Deliver early quick wins",
      ],
      outcome: ["Clear direction.", "Strong foundation.", "Early momentum."],
    },
    {
      number: "02",
      title: "AI Implementation & Integration",
      tagline: "Embed AI Into Delivery",
      icon: "gears",
      outcomeIcon: "trendUp",
      steps: [
        "Implement AI-native capabilities",
        "Integrate tools, data & platforms",
        "Automate & standardize workflows",
        "Upskill teams & drive adoption",
        "Measure, learn & adapt",
      ],
      outcome: [
        "Measurable improvements.",
        "Higher efficiency.",
        "Stronger quality.",
      ],
    },
    {
      number: "03",
      title: "Scale & Optimization",
      tagline: "Scale Impact. Drive Excellence.",
      icon: "rocket",
      outcomeIcon: "trophy",
      steps: [
        "Scale AI across value streams",
        "Optimize performance & cost",
        "Drive continuous innovation",
        "Leverage predictive & generative AI",
        "Institutionalize learning & excellence",
      ],
      outcome: [
        "Enterprise-wide impact.",
        "Continuous innovation.",
        "Sustainable excellence.",
      ],
    },
  ],
} as const;

/** Slide 13 — AI-Native Ecosystem. Slides 10-12 transcribed but not built. */
export const ecosystem = {
  headingLead: "AI-Native ",
  headingAccent: "Ecosystem",
  eyebrow: "Connected. Intelligent. Outcome-Driven.",
  body: [
    {
      lead: "Our ecosystem connects AI agents, platforms, data, and people across the quality value chain to deliver ",
      accent: "intelligence at every step",
      tail: ".",
    },
    {
      lead: "This integrated approach ensures end-to-end visibility, autonomous execution, and ",
      accent: "continuous improvement",
      tail: ".",
    },
  ],
  hub: {
    title: "AI Orchestration Layer",
    body: "Intelligence, Automation & Optimization",
    icon: "cube",
  },
  /** Clockwise from the top of the deck's radial diagram. */
  spokes: [
    { title: "AI Agents", body: "Autonomous Execution", icon: "brain" },
    {
      title: "Test Design & Intelligence",
      body: "AI-Powered Test Design & Prioritization",
      icon: "code",
    },
    {
      title: "Automation & Execution",
      body: "Intelligent Automation, Self-Healing",
      icon: "gear",
    },
    {
      title: "Analytics & Insights",
      body: "Real-time Visibility, Predictive Insights",
      icon: "trendUp",
    },
    { title: "People & Culture", body: "Empowered Teams, AI Fluency", icon: "users" },
    {
      title: "Quality & Risk Governance",
      body: "Intelligent Risk, Compliance & Standards",
      icon: "shieldCheck",
    },
    {
      title: "Integrations & Platforms",
      body: "ALM, CI/CD, DevOps, 3rd Party Ecosystem",
      icon: "puzzle",
    },
    {
      title: "Data & Context",
      body: "Unified Test Data, Telemetry & Insights",
      icon: "database",
    },
  ],
  benefitsLabel: "Ecosystem benefits",
  benefits: [
    {
      title: "End-to-end visibility",
      body: "Complete transparency across the lifecycle",
      icon: "trendUp",
    },
    {
      title: "Autonomous execution",
      body: "AI agents execute, adapt, and optimize",
      icon: "gauge",
    },
    {
      title: "Risk-aware quality",
      body: "Intelligent risk detection and mitigation",
      icon: "shieldCheck",
    },
    {
      title: "Faster time to value",
      body: "Accelerated delivery with predictable quality",
      icon: "rocket",
    },
    {
      title: "Empowered teams",
      body: "AI augmentation enables human potential",
      icon: "user",
    },
  ],
} as const;

/** Slide 14 — End-to-End Model. */
export const endToEndModel = {
  headingLead: "End-to-End ",
  headingAccent: "Model",
  eyebrow: "One System. End-to-End Quality. From Requirements to Value.",
  bodyLead: "Our Quality Control System covers the ",
  bodyAccentOne: "entire lifecycle",
  bodyMid: " — ensuring requirements are validated, risks are managed, and ",
  bodyAccentTwo: "measurable quality",
  bodyTail: " is delivered at every step.",
  stages: [
    {
      number: "01",
      title: "Requirements & Planning",
      icon: "fileText",
      items: [
        "Requirements intake",
        "Acceptance criteria",
        "Test strategy",
        "Risk assessment",
        "Test planning",
      ],
    },
    {
      number: "02",
      title: "Test Design & Preparation",
      icon: "layers",
      items: [
        "Test case design",
        "Data preparation",
        "Environment setup",
        "Reusability",
        "Traceability mapping",
      ],
    },
    {
      number: "03",
      title: "Execution & Automation",
      icon: "code",
      items: [
        "Manual testing",
        "Automated testing",
        "API & Service testing",
        "Test execution",
        "Defect logging",
      ],
    },
    {
      number: "04",
      title: "Validation & Quality Gates",
      icon: "shieldCheck",
      items: [
        "Quality gates",
        "Defect triage",
        "Risk validation",
        "Non-functional testing",
        "Release readiness",
      ],
    },
    {
      number: "05",
      title: "Reporting & Insights",
      icon: "barChart",
      items: [
        "Real-time dashboards",
        "Quality metrics",
        "Trend analysis",
        "Predictive insights",
        "Stakeholder reporting",
      ],
    },
    {
      number: "06",
      title: "Release & Value Realization",
      icon: "rocket",
      items: [
        "Release decision",
        "Production monitoring",
        "Feedback loop",
        "Continuous improvement",
        "Business value delivery",
      ],
    },
  ],
  enablersLabel: "Enablers across the model",
  enablers: [
    { title: "AI-Native Intelligence", icon: "brain" },
    { title: "Automation at Scale", icon: "gear" },
    { title: "Unified Data & Integration", icon: "database" },
    { title: "Governance & Compliance", icon: "shieldCheck" },
    { title: "People, Culture & Collaboration", icon: "users" },
    { title: "Continuous Improvement", icon: "infinity" },
  ],
} as const;

/**
 * Slide 17 — Today vs Future State team model. Slides 15 and 16 not used.
 *
 * Two deck artefacts, both flagged to the client:
 *
 * - The slide's own H2 and subhead read "Strategy to Value" / "Traceable.
 *   Transparent. Aligned." — carried over from slide 15 and unrelated to this
 *   content, which is about the team model. Replaced with a heading that matches
 *   what the slide actually says; the original is kept below for reference.
 * - Its "Key Benefits" rail is character-identical to slide 16's, which is not
 *   built. The rail is kept here so the content is not lost.
 */
export const teamModel = {
  /** Deck reads "Strategy to Value", duplicated from slide 15. */
  deckHeading: "Strategy to Value",
  headingLead: "From Traditional QA to an ",
  headingAccent: "AI-Native Quality Team",
  eyebrow: "People First. Augmented, Not Replaced.",
  body: [
    {
      lead: "We combine human expertise with AI capabilities to build high-performing teams that deliver quality at scale.",
      accent: "",
      tail: "",
    },
  ],
  bodyTwo: {
    lead: "Our model ",
    accents: ["upskills", "augments", "unlocks"],
  },
  todayLabel: "Today",
  todayTitle: "Traditional QA Team",
  futureLabel: "Future State",
  futureTitle: "AI-Native Quality Team",
  /** Paired 1:1, in the deck's order. */
  shifts: [
    {
      today: {
        title: "Manual & Repetitive Work",
        body: "High effort on low-value tasks",
        icon: "user",
      },
      future: {
        title: "Augmented Intelligence",
        body: "AI handles routine, humans focus on value",
        icon: "sparkle",
      },
    },
    {
      today: {
        title: "Siloed & Reactive",
        body: "Testing happens late and in isolation",
        icon: "clock",
      },
      future: {
        title: "Collaborative & Proactive",
        body: "Quality built-in, early and continuous",
        icon: "users",
      },
    },
    {
      today: {
        title: "Limited Visibility",
        body: "Hard to measure quality and predict risks",
        icon: "barChart",
      },
      future: {
        title: "Data-Driven Decisions",
        body: "Real-time insights, predictive quality",
        icon: "trendUp",
      },
    },
    {
      today: {
        title: "Skill Gaps",
        body: "Slow learning curve, hard to scale",
        icon: "user",
      },
      future: {
        title: "Continuous Growth",
        body: "Upskilled team, modern tools, career acceleration",
        icon: "graduation",
      },
    },
  ],
} as const;

/**
 * Key benefits. Its own section rather than a rail inside the team-model slide.
 *
 * The deck prints this rail on both slide 16 and slide 17, character for
 * character. It is held once here so a future slide-16 build reuses it instead
 * of duplicating it.
 */
export const keyBenefits = {
  label: "Key benefits",
  items: [
    {
      title: "Smarter Test Design",
      icon: "brain",
      body: "AI finds what matters most so nothing critical is missed.",
    },
    {
      title: "Faster Creation",
      icon: "clock",
      body: "Generate high-quality test scenarios and cases in minutes.",
    },
    {
      title: "Risk-Focused",
      icon: "shieldCheck",
      body: "Focus testing on high-risk areas with maximum business impact.",
    },
    {
      title: "Adaptive & Self-Learning",
      icon: "trendUp",
      body: "Continuously learns from results to improve future test design.",
    },
    {
      title: "Human + AI Synergy",
      icon: "users",
      body: "AI augments testers, testers bring judgment. Together we win.",
    },
  ],
} as const;

/** Slide 4 — AI Alone Doesn't Transform Delivery */
export const aiAloneDoesnt = {
  headingLead: "AI Alone Doesn't ",
  headingAccent: "Transform Delivery",
  subheadLead: "Tools Don't Change Organizations. ",
  subheadAccent: "Operating Models",
  subheadTail: " Do.",
  points: [
    {
      lead: "AI tools amplify what already exists. If your processes are broken, ",
      accent: "AI will only make broken faster.",
      tail: "",
    },
    {
      lead: "Real transformation happens when AI is embedded into the right processes, empowered by knowledge, automation, and a ",
      accent: "modern operating model.",
      tail: "",
    },
  ],
  versus: "VS",
  panels: [
    {
      title: "AI Without Transformation",
      highlight: false,
      steps: [
        { label: "AI Tool", result: "Standalone tool" },
        { label: "Engineer", result: "Individual effort" },
        { label: "Same Process", result: "Old flows & bottlenecks" },
        { label: "Limited Improvement", result: "Incremental results" },
      ],
    },
    {
      title: "AI With Transformation",
      highlight: true,
      steps: [
        { label: "AI Capabilities", result: "Integrated intelligence" },
        { label: "AI-Native Processes", result: "Designed for AI & humans" },
        { label: "Knowledge Platform", result: "Contextual knowledge" },
        { label: "Automation & Agents", result: "Intelligent automation" },
        { label: "Business Transformation", result: "Measurable impact" },
      ],
    },
  ],
} as const;
